import React, { useEffect, useState } from "react";
import axios, { CancelToken } from "axios";
import { useTranslation } from "react-i18next";

import { StatusCode } from "api/protocols";

import { GetProduct as GetClientProduct } from "api/rpc/2022-09/clientAdmin/product/product";

import { IProduct, IVariant } from "redux/reducers/models/product";

import Sheet from "components/sheet/Sheet";
import DataTable from "../../../../../components/dataTable/DataTable";
import Search from "components/search/Search";
import { useAppDispatch } from "hooks/redux";
import { searchHistoryClear } from "redux/actions/searchBars/searchHistory";
import { GetProduct as GetFacilityProduct } from "api/rpc/2024-04/facilityAdmin/product/product";

type AttachProductAsComboProps = {
  isVisible: boolean;
  onCancel: () => void;
  onOk: (variantId: number, variant?: IVariant, product?: IProduct) => void;
  userLevel: "facility" | "client";
  currentlyAddedProducts?: Record<string, any>[];
  useOkAsCancel?: boolean;
};

const TABLE_LIMIT = 25;

/** Handles Adding a Product to another Product. */
export default function AttachProductAsCombo(props: AttachProductAsComboProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const [filterState] = useState({
    search: "",
    sort: "alpha",
  });

  const [allProducts, setAllProducts] = useState<IProduct[]>(null);

  // Define filter presets here
  const [filters, setFilters] = useState({
    search: undefined,
    searching: true,
    offset: 0,
  });

  useEffect(() => {
    const source = axios.CancelToken.source();

    if (filters.search !== undefined) {
      switch (props.userLevel) {
        case "facility": {
          if (filters.search !== undefined) {
            void searchFacilityProducts(source.token);
          }
          break;
        }
        case "client": {
          if (filters.search !== undefined) {
            void searchClientProducts(source.token);
          }

          break;
        }
        default: {
          return;
        }
      }
    }

    setFilters(prevState => ({ ...prevState, searching: false }));

    return () => {
      source.cancel();
    };
  }, [filters.search, filters.offset]);

  async function searchFacilityProducts(token: CancelToken) {
    setAllProducts(null);

    const { status, data } = await GetFacilityProduct(
      {
        extended: true,
        extended_variants: true,
        inventory: true,
        search: filters.search,
        limit: TABLE_LIMIT,
        offset: filters.offset,
      },
      false,
      token,
    );

    if (status === StatusCode.OK) {
      setAllProducts(data);
    }
  }

  async function searchClientProducts(token: CancelToken) {
    setAllProducts(null);

    const { status, data } = await GetClientProduct(
      {
        extended: true,
        extended_variants: true,
        inventory: true,
        search: filters.search,
        limit: TABLE_LIMIT,
        offset: filters.offset,
      },
      false,
      token,
    );

    if (status === StatusCode.OK) {
      setAllProducts(data);
    }
  }

  function onCancel() {
    setFilters(prevState => ({ ...prevState, search: undefined }));
    dispatch(searchHistoryClear("combo-products-search"));
    props.onCancel();
  }

  function handleTableNavigation(direction: "prev" | "next") {
    switch (direction) {
      case "prev": {
        setFilters(prev => ({ ...prev, offset: prev.offset - TABLE_LIMIT }));
        return;
      }
      case "next": {
        setFilters(prev => ({ ...prev, offset: prev.offset + TABLE_LIMIT }));
        return;
      }
      default:
        return;
    }
  }

  return (
    <Sheet
      open={props.isVisible}
      title={t("secure.facility.product.product.076")}
      size="medium"
      closable
      onCancel={onCancel}
      cancelText={t("secure.facility.product.product.077")}
      backDropCancel={false}
      onOk={props.useOkAsCancel ? onCancel : undefined}
    >
      <div className="flex-grow mb-4 pt-4">
        <Search
          searchCallback={searchValue => setFilters(prev => ({ ...prev, search: searchValue, offset: 0 }))}
          historyKey="combo-products-search"
          placeholder={t("secure.facility.product.products.003")}
        />
      </div>

      <DataTable
        columns={[
          { label: t("Name"), width: "50%" },
          { label: t("SKU"), width: "25%" },
          { label: "", width: "25%" },
        ]}
        loading={!allProducts}
        footer={{
          tableLimit: TABLE_LIMIT,
          tableOffset: filters.offset,
          handleTableOffset: direction => handleTableNavigation(direction),
          disableNextOffset: !allProducts || !(allProducts?.length === TABLE_LIMIT),
        }}
      >
        {allProducts
          ?.filter(product => product.type !== "Combo")
          ?.sort((firstProd: any, secondProd: any) => {
            if (filterState.sort === "alpha") {
              if (firstProd.title > secondProd.title) {
                return 1;
              }
              if (firstProd.title < secondProd.title) {
                return -1;
              }
              return 0;
            }
          })
          ?.map((product: IProduct) => {
            const disableClick =
              product.variant_count > 1 || props.currentlyAddedProducts?.some(val => val.product_id === product.id);

            return (
              <React.Fragment key={`product_${product.id}`}>
                {/* Product only clickable if variant_count is 0 */}
                <tr
                  key={product.id}
                  onClick={() =>
                    disableClick ? undefined : props.onOk(product.variants[0].id, product.variants[0], product)
                  }
                  className={`${disableClick ? "" : "clickable"}`}
                >
                  <td>
                    <p style={{ textAlign: "left", fontSize: "14px", fontWeight: 600 }}>{`${product.title}${
                      product.subtitle ? " - " + product.subtitle : ""
                    }`}</p>
                  </td>
                  <td>{product.variant_count > 1 ? "" : product?.variants[0]?.sku}</td>
                  {product.variants.length <= 1 && !disableClick ? (
                    <td style={{ borderColor: "none" }}>
                      <p>{t("secure.facility.product.product.079")}</p>
                    </td>
                  ) : (
                    <td></td>
                  )}
                </tr>
                {/* If variants present, display as clickable row instead */}
                {product.variants.length > 1
                  ? product.variants.map(variant => {
                      const disableVariantClick = props.currentlyAddedProducts?.some(val => val.id === variant.id);

                      return (
                        <tr
                          key={variant.id}
                          className={disableVariantClick ? "" : "clickable"}
                          onClick={() => (disableVariantClick ? undefined : props.onOk(variant.id, variant, product))}
                        >
                          <td>
                            <p style={{ fontStyle: "italic", display: "block", marginLeft: "24px" }}>{variant.title}</p>
                          </td>
                          <td>{variant?.sku}</td>
                          <td style={{ borderColor: "none" }}>
                            <p>{t("secure.facility.product.product.079")}</p>
                          </td>
                        </tr>
                      );
                    })
                  : null}
              </React.Fragment>
            );
          })}
      </DataTable>
    </Sheet>
  );
}
